import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import './ReportDetails.css';

const API_BASE_URL = 'http://72.61.235.16:5001/api/v1';

const ReportDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [report, setReport] = useState(location.state?.report || null);
  const [loading, setLoading] = useState(!location.state?.report);

  useEffect(() => {
    if (!report) {
      fetchReportDetails();
    }
  }, [id]);

  const fetchReportDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/reports/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data.data.report);
    } catch (error) {
      console.error('Error fetching report:', error);
      alert('Failed to load report details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return '#10b981';
      case 'rejected':
        return '#ef4444';
      case 'pending':
      default:
        return '#f59e0b';
    }
  };

  const handleDownloadReport = () => {
    if (!report) return;

    try {
      const pdf = new jsPDF();
      
      // Add title
      pdf.setFontSize(20);
      pdf.setTextColor(17, 74, 166);
      pdf.text('Site Report', 20, 30);
      
      // Add project info
      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Project: ${report.project_name || 'N/A'}`, 20, 50);
      pdf.text(`Location: ${report.project_location || 'N/A'}`, 20, 65);
      pdf.text(`Created: ${new Date(report.created_at).toLocaleDateString()}`, 20, 80);
      
      // Add report details
      pdf.setFontSize(14);
      pdf.text('Report Details:', 20, 105);
      
      pdf.setFontSize(12);
      pdf.text(`Total Floors: ${report.total_floors}`, 25, 120);
      pdf.text(`Dimensions: ${report.dimensions}`, 25, 135);
      pdf.text(`Approval Status: ${(report.approval_status || 'pending').toUpperCase()}`, 25, 150);
      
      if (report.remarks) {
        pdf.text('Remarks:', 25, 170);
        const splitRemarks = pdf.splitTextToSize(report.remarks, 160);
        pdf.text(splitRemarks, 25, 185);
      }
      
      // Add images info if available
      if (report.images && report.images.length > 0) {
        const yPos = report.remarks ? 210 : 170;
        pdf.text(`Site Images: ${report.images.length} image(s) available`, 25, yPos);
        pdf.text('(Images can be viewed in the web application)', 25, yPos + 15);
      }
      
      // Save the PDF
      pdf.save(`site-report-${report.project_name?.replace(/\s+/g, '-') || 'report'}-${Date.now()}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="page-container">
        <div className="error-container">
          <i className="fas fa-exclamation-circle"></i>
          <p>Report not found</p>
          <button className="btn-primary" onClick={() => navigate('/reports')}>
            Back to Reports
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <button className="btn-back" onClick={() => navigate('/reports')}>
            <i className="fas fa-arrow-left"></i> Back
          </button>
          <h1 className="page-title">Site Report</h1>
          <p className="page-subtitle">
            {report.project_name || 'N/A'} • Created: {new Date(report.created_at).toLocaleDateString()}
          </p>
        </div>
        <button className="btn-download" onClick={handleDownloadReport}>
          <i className="fas fa-download"></i> Download Report
        </button>
      </div>

      {/* Report Details */}
      <div className="details-grid">
        <div className="details-card">
          <h3 className="card-title">Report Information</h3>
          <div className="detail-row">
            <span className="detail-label">Project Name</span>
            <span className="detail-value">{report.project_name || 'N/A'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Location</span>
            <span className="detail-value">{report.project_location || 'N/A'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Total Floors</span>
            <span className="detail-value">{report.total_floors}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Dimensions</span>
            <span className="detail-value">{report.dimensions}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Approval Status</span>
            <span 
              className="detail-value status-badge"
              style={{ backgroundColor: getStatusColor(report.approval_status) }}
            >
              {(report.approval_status || 'pending').toUpperCase()}
            </span>
          </div>
          {report.remarks && (
            <div className="detail-row">
              <span className="detail-label">Remarks</span>
              <span className="detail-value">{report.remarks}</span>
            </div>
          )}
          <div className="detail-row">
            <span className="detail-label">Created Date</span>
            <span className="detail-value">
              {new Date(report.created_at).toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* Images Section */}
      {report.images && report.images.length > 0 && (
        <div className="images-section">
          <h3 className="section-title">Site Images ({report.images.length})</h3>
          <div className="images-grid">
            {report.images.map((img, idx) => {
              const imageUrl = `http://localhost:5000${img}`;
              return (
                <div key={idx} className="image-container">
                  <img 
                    src={imageUrl}
                    alt={`Site Image ${idx + 1}`}
                    className="report-image"
                    onError={(e) => {
                      console.error('Failed to load image:', imageUrl);
                      e.target.style.display = 'none';
                    }}
                    onClick={() => window.open(imageUrl, '_blank')}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportDetails;
